import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { UserAvailability } from '../../../../models/UserAvailability';
import { UserAvailabilityService } from '../../../../services/user-availability.service';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Component, OnInit } from '@angular/core';
import { UtilsService } from '../../../../services/utils.service';
import { TranslatePipe } from '@ngx-translate/core';
import { CalendarInputComponent } from '../../../shared/calendar-input/calendar-input.component';
import { ButtonDirective } from 'primeng/button';
import { Textarea } from 'primeng/textarea';

@Component({
  selector: 'app-availability-dialog',
  templateUrl: './availability-dialog.component.html',
  styleUrls: ['./availability-dialog.component.scss'],

  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    CalendarInputComponent,
    ButtonDirective,
    Textarea
  ]
})
export class AvailabilityDialogComponent implements OnInit {
  public info: any;
  public userAvailability: UserAvailability | null = null;
  public availabilityForm: UntypedFormGroup;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private userAvailabilityService: UserAvailabilityService,
    private formBuilder: UntypedFormBuilder,
    private utilsService: UtilsService
  ) {
    this.availabilityForm = new UntypedFormGroup({});
    this.availabilityForm = this.formBuilder.group({
      availabilityStart: ['', Validators.required],
      availabilityEnd: ['', Validators.required],
      type: [''],
      description: ['']
    });
  }

  // Parse DB trigger error for overlapping availability and extract timestamps if present
  private parseOverlapError(err: any): {
    isOverlap: boolean;
    message?: string;
    start?: string;
    end?: string;
  } {
    const res = {
      isOverlap: false as boolean,
      message: undefined as string | undefined,
      start: undefined as string | undefined,
      end: undefined as string | undefined
    };
    // If this is an Angular HttpErrorResponse, server payload is under err.error
    const payload = err?.error ?? err;

    // originalError is used by server wrapper; try multiple nested locations
    const orig = payload?.originalError || payload?.original || payload;
    const parent = orig?.parent || orig;

    const text = (
      parent?.text ||
      parent?.message ||
      orig?.message ||
      payload?.message ||
      err?.message ||
      ''
    ).toString();
    if (text && text.toLowerCase().includes('overlapping availability')) {
      res.isOverlap = true;
      res.message = 'Overlapping availability exists for this user';
      // parameters array lives on several nested objects in different errors
      const params =
        parent?.parameters ||
        orig?.parameters ||
        payload?.originalError?.parent?.parameters ||
        payload?.original?.parent?.parameters ||
        [];
      if (Array.isArray(params) && params.length >= 5) {
        res.start = params[3];
        res.end = params[4];
      }
      return res;
    }

    if (parent?.sqlState === '45000' || parent?.errno === 1644) {
      res.isOverlap = true;
      res.message = 'Database signaled a constraint violation';
    }
    return res;
  }

  ngOnInit(): void {
    this.info = this.config.data.info;
    this.userAvailability = this.config.data.userAvailability;
    this.initForm();
  }

  private initForm(): void {
    if (this.userAvailability) {
      this.availabilityForm.patchValue({
        availabilityStart: this.userAvailability.availabilityStart,
        availabilityEnd: this.userAvailability.availabilityEnd,
        type: this.userAvailability.type || '',
        description: this.userAvailability.description || ''
      });
    }
  }

  public doClose(): void {
    this.ref.close(null);
  }

  public handleDateChange(newDate: Date, type: string): void {
    if (type === 'startDate') {
      this.availabilityForm.patchValue({
        availabilityStart: newDate
      });
    } else if (type === 'endDate') {
      this.availabilityForm.patchValue({
        availabilityEnd: newDate
      });
    }
  }

  public selectType(newType: string): void {
    this.availabilityForm.patchValue({
      type: newType
    });
  }

  public saveAvailableTime(): void {
    const formData = this.availabilityForm.getRawValue();
    if (
      this.userAvailability &&
      formData &&
      formData.availabilityStart &&
      formData.availabilityEnd
    ) {
      const availabilityInfo = {
        ...this.userAvailability,
        availabilityStart: formData.availabilityStart,
        availabilityEnd: formData.availabilityEnd,
        type: formData.type || '',
        description: formData.description || ''
      };

      this.userAvailabilityService
        .saveUserAvailability(availabilityInfo)
        .subscribe(
          (res) => {
            const retValue = {
              type: 'newAvailability',
              data: res
            };
            if (availabilityInfo.id) {
              retValue.type = 'updateAvailability';
            }
            this.ref.close(retValue);
          },
          (err) => {
            const parsed = this.parseOverlapError(err);
            if (parsed.isOverlap) {
              // Convert SQL timestamp format ("YYYY-MM-DD HH:mm:ss.sss") to ISO-like for Date parsing
              const fmt = (s?: string) => {
                if (!s) return null;
                const iso = s.replace(' ', 'T');
                const d = new Date(iso);
                return isNaN(d.getTime()) ? s : d.toLocaleString();
              };
              const startDisplay = fmt(parsed.start);
              const endDisplay = fmt(parsed.end);
              const times =
                startDisplay && endDisplay
                  ? ` (${startDisplay} - ${endDisplay})`
                  : '';
              this.utilsService.showError(
                `Selected time overlaps existing availability${times}.`
              );
            } else {
              const userMsg =
                err?.error?.message || err?.message || 'Save failed';
              this.utilsService.showError(userMsg);
            }
          }
        );
    }

    /*
    if (this.userAvailability && this.userAvailability.availabilityStart
      && this.userAvailability.availabilityEnd && this.userAvailability.userId) {
        this.userAvailabilityService.saveUserAvailability(this.userAvailability).subscribe(res => {
          console.log(res);
          const retValue = {
            type: 'newAvailability',
            data: res
          }
          this.ref.close(retValue);
        });
    }
    */
  }

  public onDelete(): void {
    if (this.userAvailability && this.userAvailability.id) {
      this.userAvailabilityService
        .deleteUserAvailability(this.userAvailability.id)
        .subscribe((res) => {
          const retValue = {
            type: 'delAvailability',
            data: {
              id: this.userAvailability?.id,
              event: this.info.event
            }
          };
          this.ref.close(retValue);
        });
    }
  }
}
